import React, { useRef } from "react";

import { newID } from "../utils/unique";
import { MessageHandler, broadcast, useChannel } from "./useChannel";
import { getObjectValue, setObjectValue } from "../utils/objectPath";
import { isObject } from "rant-utils";


export interface Error {
    message: string,
    prop?: string,
    isFromServer?: boolean,
	
	display?: boolean,
}

export interface ValidationContext<T> {
    restrictTo?: string[],

    data: DataState<T>,
    errors: Error[],
}

export interface Validator<T> {
    validate: (context: ValidationContext<T>) => boolean | void,
}

export interface DataState<T> {

    id: string,
    channelName: string,

    value: T,

    errors: Error[],

	validateCount: number,

	parentContext?: DataState<T>,

	options?: any, // general purpose spot to store things

    clear: () => void,

    getValue: (prop: string) => any,
    setValue: (prop: string, val: any, force?: boolean) => void,
    setValues: (values: any, force?: boolean) => void,

	hasErrors: (prop?: string) => boolean,
	getErrors: (prop?: string) => Error[],

    validators?: Validator<T>[],

    validate: (restrictTo?: string[]) => Error[],

	setApiStatus: (status: { communicating: boolean }) => void,

	customValidate?: (context: ValidationContext<T>) => void,
}

export function getDataChannelName(id: string) {
    return "data/" + id;
}

export const DataMessages = {
    CHANGE: "change",

	VALIDATE: "validate/custom",

    VALIDATION_RESULT: "validation/result",

    ERRORS_CHANGE: "errors/change",

    API_STATUS: "api/status",

}

export function notifyErrorsChanged(data: DataState<any>) {
    broadcast(data.channelName, {
        type: DataMessages.ERRORS_CHANGE,
        data,
    })
}

export function notifyChange(
    data: DataState<any>,
    prevValues: any,
    newValues: any,
    propsChanged: string[],
) {
    broadcast(data.channelName, {
        type: DataMessages.CHANGE,
        data,
        prevValues,
		newValues,
        propsChanged,
		//value: ((propsChanged.length === 1) ? newValues[propsChanged[0]] : undefined),
    })
}

function createDataState<T>(props: {
    initialValue?: T,
    validators?: Validator<T>[],
    customID?: string,
}) {

    const id = props.customID ? props.customID : newID();
    const channelName = getDataChannelName(id);
    const value = props.initialValue ? props.initialValue : {} as T;

    const result: DataState<T> = {

        id,
        channelName,

        errors: [],

        value,

		validateCount: 0,

        validators: props.validators,

		setApiStatus: function(status: { communicating: boolean }) {
			broadcast(this.channelName, {
				type: DataMessages.API_STATUS,
				status,
			});
		},

        clear: function() {
            this.value = value;
        },

		getErrors: function(prop?: string) {
			if (!prop) return this.errors;

			const result = [];
			for(let e of this.errors) {
				if (e.prop === prop) result.push(e);
			}
			return result;
		},
		hasErrors: function(prop?: string) {
			if (prop) {
				return (this.getErrors().length > 0);
			} else {
				return (this.errors.length > 0);
			}
		},

        setValue: function(prop: string, value: any, force?: boolean) {
            const prev = this.getValue(prop);

            // has it changed?
            if (prev !== value || force) {
				setObjectValue(this.value, prop, value);

                let prevValues = {} as any;
				prevValues[prop] = prev;
				let newValues = {} as any;
				newValues[prop] = value;

                notifyChange(this, prevValues, newValues, [prop]);

                this.validate();
            }
        },

        setValues: function(values: any, force?: boolean) {
            const propsChanged = [];
            const prevValues = {} as any;
			let newValues = {} as any;

            let hasChanges = false;
            for(let prop in values) {
				const prevVal = this.getValue(prop);
				const newVal = getObjectValue(values, prop);

                if (prevVal !== newVal) {
                    hasChanges = true;

                    prevValues[prop] = prevVal;
					newValues[prop] = newVal;
                    propsChanged.push(prop);

					setObjectValue(this.value, prop, newVal);
                }
            }

            if (hasChanges || force) {
                notifyChange(this, prevValues, newValues, propsChanged);
                this.validate(propsChanged);
            }
        },

        getValue: function(prop: string) {
			return getObjectValue(this.value, prop);
        },

        validate: function(restrictTo?: string[]) {

			this.validateCount++;

            const context: ValidationContext<T> = {
                data: this,
                restrictTo,
                errors: [],
            }

            if (this.validators) {
                for(let v of this.validators) {
                    v.validate(context);
                }
            }

			// ask anyone listening if they want to contribute
			broadcast(this.channelName, {
				type: DataMessages.VALIDATE,
				context,
			});

			// custom 
			if (this.customValidate) {
				this.customValidate(context);
			}

            broadcast(this.channelName, {
                type: DataMessages.VALIDATION_RESULT,
                data: this,
                errors: context.errors,
            });
            
			if (
				(context.errors.length > 0 || context.errors.length !== this.errors.length)
				|| (context.errors.length === 0 && this.validateCount === 1)
			) {
				this.errors = context.errors;
				notifyErrorsChanged(this);
			}
		
            return context.errors;
        },


    };

    return result;
}

export const DataContext = React.createContext<DataState<any>>({} as DataState<any>);

export function useData<T>() {
    return React.useContext(DataContext) as DataState<T>;
}

export function useDataContext<T>(props: {
    initialValue?: T,
    value?: T,
    customID?: string,
    validators?: Validator<T>[],
	parentContext?: DataState<any>,
}) {

    const { initialValue, value, customID, validators } = props;

    const context = React.useMemo(() => {
        return createDataState<T>({
            initialValue,
            customID,
            validators,
        });
// eslint-disable-next-line react-hooks/exhaustive-deps        
    }, [customID, validators]);

    if (value !== undefined) {
        context.value = value;
    }

	if (props.parentContext !== context.parentContext) {
		context.parentContext = props.parentContext;
	}

    return context;
}

// NOTE: always useCallback on handler
export function useDataEvents(data: DataState<any>, handler: MessageHandler, disable?: boolean) {
    useChannel(data.channelName, handler, !disable);
}

// this is a specifically typed version of useDataEvents to make it more streamlined for handling custom validation
// NOTE: always useCallback on handler
export function useValidation<T>(data: DataState<T>, handler: (msg: ValidationContext<T>) => void) {
	useChannel(data.channelName, React.useCallback((msg: any) => {
		if (msg.type === DataMessages.VALIDATE) {
			handler(msg.context);
		}
	}, [handler]));
}

export function getErrors(data: DataState<any>, prop?: string) {
    const { errors, } = data;

    const result = [];

    for(let e of errors) {
        if (!prop || e.prop === prop) {
            result.push(e);
        }
    }

    return result;
}

// disable flag is used so can conditionally not have plumbing hooked up when required in form component
export function useErrors(data: DataState<any>, prop?: string, disable?: boolean) {
	
	const errorsRef = useRef(
		disable ? [] : ((data && data.getErrors) ? data.getErrors(prop) : [])
	);

    const [errors, setErrors] = React.useState<Error[]>(disable ? [] : ((data && data.getErrors) ? data.getErrors(prop) : []));

    useDataEvents(
        data,
        React.useCallback((msg: any) => {
            if (msg.type === DataMessages.ERRORS_CHANGE) {
                const latest = getErrors(data, prop);

				// has anything actually changed?
				if (latest.length === errorsRef.current.length) {
					let foundDiff = false;
					for(let i=0;i<latest.length;i++) {
						const e1 = latest[i];
						const e2 = errorsRef.current[i];
						if (e1.prop !== e2.prop || e1.message !== e2.message) {
							foundDiff = true;
							break;
						}
					}
					if (!foundDiff) return;
				}

                setErrors(latest);
				errorsRef.current = latest;
            }
        }, [data, setErrors, prop]),
		disable,
    );

    return errors;
}

export function useDataValue(data: DataState<any>, prop: string) {
    const [, setState] = React.useState({
        value: data.getValue(prop),
    });

    useDataEvents(
        data,
        React.useCallback(
            (msg: any) => {

				if (msg.type === DataMessages.CHANGE) {

					if (msg.prevValues && msg.newValues && !msg.prevValues.shareholder && msg.newValues.shareholder) {
						debugger;
					}

					const changes: string[] = msg.propsChanged;
					if (prop.indexOf(".") >= 0) {
						// need to check for parent objects changing
						let hasChanged = false;
						for(let c of changes) {
							if (prop === c || prop.startsWith(c + ".")) {
								hasChanged = true;
								break;
							}
						}
						if (!hasChanged) return;

					} else if (changes.indexOf(prop) < 0) {
						return;
					}

                    setState({
                        value: msg.value,
                    })
                }
            }, 
            [prop]
        )
    );

    return data.getValue(prop);
}
